import React, { useState } from "react";
import styled from "styled-components";
import { FontSize, GlossyThemeProvider, Spacing, useTheme } from "@glossy";
import { Timezone } from "@linkfluence/models/timezone";
import Icon from "@ui-kit/icon";
import Modal from "@ui-kit/modal/fullscreen";
import ErrorCard from "@radarly/components/error-boundary/card";
import Overlay from "@radarly/components/filterbar/overlay";
import Form from "@radarly/components/panels/card-edition/form";
import { useCardEditor } from "@radarly/core/cards/use-cases";
import { useContextualFocuses } from "@radarly/core/context/use-cases";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { tr } from "@radarly/i18n";
import { Card } from "@radarly/models/cards";
import { editDatavizOptionsOnCard } from "@radarly/models/cards/editor";
import { isFilterableTile, useCardLimits } from "@radarly/models/cards/is";
import { Clause } from "@radarly/models/clause";
import CardComponent from "@radarly/shared/cards";
import { ErrorBoundary, ErrorFilterbar } from "@radarly/shared/error-boundary";
import Header from "./header";
import Sidebar from "./sidebar";

interface SharedPanelsCardEditionFormProps {
  card: Card;
  timezone: Timezone;
  aiContext: string;
  setAIContext: (description: string) => void;
  setCard: (card: Card) => void;
  onClose: () => void;
  onValidate: () => void;
  onPasteFilters: (clause: Clause) => void;
  pageColors?: string[];
}

const ErrorStyled = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.ExtraSmall};
  font-size: ${FontSize.Medium};
`;

export default function SharedPanelsCardEditionForm({
  card,
  setCard,
  onClose,
  onValidate,
  onPasteFilters,
  aiContext,
  setAIContext,
  timezone,
  pageColors,
}: SharedPanelsCardEditionFormProps) {
  const theme = useTheme();
  const locale = useCurrentUserLocale();
  const {
    cardDraftClause,
    hasCardDraftClause,
    onSubmitCardClause,
    onSubmitCardDraftClause,
    onCancelCardDraftClause,
    setCardDraftClause,
    onClosePanelEditor,
    hasCardClause,
    setCardPeriod,
    period,
  } = useCardEditor(card, setCard);
  const hasHeader = isFilterableTile(card);
  const focuses = useContextualFocuses();
  const limit = useCardLimits(card, focuses);

  const [localAIContext, setLocalAIContext] = useState(aiContext);

  const setLocalAndIpDescription = (aiContext: string) => {
    setAIContext(aiContext);
    setLocalAIContext(aiContext);
  };

  return (
    <Modal
      title={tr(locale, "CUSTOM_PAGES::EDITOR::CARD_PANEL_TITLE")}
      onClose={onClose}
      onValidate={onValidate}
      isValid={!limit}
      validateTooltip={
        limit
          ? tr(locale, "CUSTOM_PAGES::EDITOR::ERROR_TOOLTIP_MESSAGE::" + limit)
          : ""
      }
      options={
        !!limit && (
          <ErrorStyled>
            <Icon
              glyph="bang-fat"
              size="s"
              disk
              color={theme.message.warning}
            />
            {tr(locale, "CUSTOM_PAGES::EDITOR::ERROR_HEADER_MESSAGE")}
          </ErrorStyled>
        )
      }
      subheader={
        hasHeader ? (
          <ErrorBoundary
            fallback={
              <ErrorFilterbar onReset={() => onSubmitCardClause(null)} />
            }
          >
            <Header
              onPasteFilters={onPasteFilters}
              clause={cardDraftClause}
              period={period}
              setClause={setCardDraftClause}
              applyClause={onSubmitCardClause}
              onApply={onSubmitCardDraftClause}
              setPeriod={setCardPeriod}
              onClose={onClosePanelEditor}
              hasClause={hasCardClause}
              timezone={timezone}
              card={card}
            />
          </ErrorBoundary>
        ) : null
      }
    >
      <Form
        onClickBack={() => setCard(null)}
        card={
          <GlossyThemeProvider name="tile">
            <ErrorBoundary
              fallback={<ErrorCard locale={locale} title={card.title} />}
            >
              <CardComponent
                card={card}
                height={350}
                timezone={timezone}
                setCard={setCard}
                setDataviz={(dataviz) =>
                  setCard(editDatavizOptionsOnCard(card, dataviz))
                }
                aiContext={localAIContext}
                forceReload
                readOnly
                isEdition
              />
            </ErrorBoundary>
          </GlossyThemeProvider>
        }
        sidebar={
          <Sidebar
            card={card}
            setCard={setCard}
            pageColors={pageColors}
            aiContext={localAIContext}
            setAIContext={setLocalAndIpDescription}
          />
        }
        overlay={
          hasCardDraftClause && (
            <GlossyThemeProvider name="body">
              <Overlay
                locale={locale}
                onSubmit={onSubmitCardDraftClause}
                onCancel={onCancelCardDraftClause}
              />
            </GlossyThemeProvider>
          )
        }
        locale={locale}
      />
    </Modal>
  );
}
